/**
 * Google Analytics 4 (gtag.js), gated on configuration and cookie consent.
 *
 * Nothing loads and nothing is sent unless all of these hold:
 * - REACT_APP_GA4_ID is set at build time and looks like a GA4 measurement
 *   ID (G-XXXXXXXX);
 * - the app runs on the web (the Capacitor iOS app never loads GA4 and never
 *   shows the consent banner);
 * - the visitor clicked Accept on the cookie banner (stored in localStorage).
 *
 * Every exported tracking helper is a silent no-op otherwise.
 *
 * URLs: query strings and fragments never reach Google. They can carry
 * Supabase PKCE codes (/auth/callback?code=...), reset tokens or Stripe
 * session ids. gtag would otherwise read document.location on every hit, so:
 * - automatic page views are off (`send_page_view: false`);
 * - page_location / page_referrer are set to origin + pathname, at load and
 *   on every route change (`gtag('set', ...)`), and on every event we send;
 * - page views are sent by trackPageView(), called from <AnalyticsPageViews />
 *   in App.tsx on load, on route changes and after Accept.
 */
import { isNativeApp } from './platform';

export type ConsentChoice = 'granted' | 'denied';

type EventParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const CONSENT_STORAGE_KEY = 'wsle:analyticsConsent';
/** Fired on window whenever the stored choice changes (or is cleared). */
export const CONSENT_CHANGED_EVENT = 'wallstreetle-analytics-consent-changed';

const GTAG_SCRIPT_ID = 'wsle-ga4-gtag';
const SIGN_UP_SENT_PREFIX = 'wsle:ga4:signUpSent:';
const CHECKOUT_MARKER_KEY = 'wsle:ga4:checkoutStarted';
const LAST_PURCHASE_KEY = 'wsle:ga4:lastPurchaseId';

/** An OAuth account counts as new if its first sign-in is this close to creation. */
const NEW_ACCOUNT_SIGN_IN_GAP_MS = 2 * 60 * 1000;
/** ...and the account itself was created this recently. */
const NEW_ACCOUNT_MAX_AGE_MS = 10 * 60 * 1000;

let gtagLoaded = false;

// ---------------------------------------------------------------------------
// Storage helpers (private mode / disabled storage must never throw)
// ---------------------------------------------------------------------------

const readStorage = (storage: 'local' | 'session', key: string): string | null => {
  try {
    return (storage === 'local' ? window.localStorage : window.sessionStorage).getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (storage: 'local' | 'session', key: string, value: string | null): void => {
  try {
    const target = storage === 'local' ? window.localStorage : window.sessionStorage;
    if (value === null) {
      target.removeItem(key);
    } else {
      target.setItem(key, value);
    }
  } catch {
    // Ignore: analytics bookkeeping is best effort.
  }
};

// ---------------------------------------------------------------------------
// Configuration and consent
// ---------------------------------------------------------------------------

const GA4_ID_PATTERN = /^G-[A-Z0-9]{4,20}$/;

/** The configured measurement ID, or '' when unset or malformed (GA4 then stays off). */
export const getGa4Id = (): string => {
  const id = (process.env.REACT_APP_GA4_ID || '').trim();
  return GA4_ID_PATTERN.test(id) ? id : '';
};

/** GA4 is configured for this build and this is the web app (not native). */
export const isAnalyticsConfigured = (): boolean => {
  if (typeof window === 'undefined' || !getGa4Id()) {
    return false;
  }
  try {
    return !isNativeApp();
  } catch {
    return false;
  }
};

export const getConsent = (): ConsentChoice | null => {
  const value = readStorage('local', CONSENT_STORAGE_KEY);
  return value === 'granted' || value === 'denied' ? value : null;
};

/** True when the banner should be shown: GA4 is configured and no choice is stored. */
export const needsConsentPrompt = (): boolean => isAnalyticsConfigured() && getConsent() === null;

export const isAnalyticsEnabled = (): boolean => isAnalyticsConfigured() && getConsent() === 'granted';

const notifyConsentChanged = (): void => {
  try {
    window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
  } catch {
    // Ignore
  }
};

/** Store the visitor's choice. Accept loads GA4; Decline stops it if it was running. */
export const setConsent = (choice: ConsentChoice): void => {
  if (!isAnalyticsConfigured()) {
    return;
  }
  writeStorage('local', CONSENT_STORAGE_KEY, choice);

  if (choice === 'granted') {
    loadGa4();
  } else {
    disableGa4();
  }
  notifyConsentChanged();
};

/** Forget the stored choice so the banner asks again ("Cookie settings" in the footer). */
export const clearConsent = (): void => {
  if (!isAnalyticsConfigured()) {
    return;
  }
  writeStorage('local', CONSENT_STORAGE_KEY, null);
  disableGa4();
  notifyConsentChanged();
};

// ---------------------------------------------------------------------------
// URL scrubbing: only origin + pathname ever goes to Google
// ---------------------------------------------------------------------------

/** Path without query string or fragment. */
const pathOnly = (path: string): string => path.split(/[?#]/)[0] || '/';

/** origin + pathname of a URL (no query, no fragment); '' if it can't be parsed. */
export const stripUrl = (url: string): string => {
  if (!url) {
    return '';
  }
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return '';
  }
};

interface PageFields {
  page_location: string;
  page_path: string;
  page_referrer?: string;
}

const pageFields = (pathname?: string): PageFields => {
  const path = pathOnly(pathname ?? window.location.pathname);
  let referrer = '';
  try {
    referrer = stripUrl(document.referrer);
  } catch {
    referrer = '';
  }
  return {
    page_location: `${window.location.origin}${path}`,
    page_path: path,
    page_referrer: referrer || undefined,
  };
};

/** Override gtag's own document.location / document.referrer reads for later hits. */
const setPageContext = (fields: PageFields): void => {
  const context: Record<string, string> = { page_location: fields.page_location };
  if (fields.page_referrer) {
    context.page_referrer = fields.page_referrer;
  }
  window.gtag?.('set', context);
};

// ---------------------------------------------------------------------------
// GA cookies
// ---------------------------------------------------------------------------

const GA_COOKIE_PATTERN = /^_ga(_.+)?$/;
const GA_COOKIE_DOMAINS = ['.wallstreetle.com'];

/** Expire _ga and _ga_<ID> on the current host and on .wallstreetle.com. */
const expireGaCookies = (): void => {
  if (typeof document === 'undefined') {
    return;
  }
  let names: string[] = [];
  try {
    names = document.cookie
      .split(';')
      .map((cookie) => cookie.split('=')[0].trim())
      .filter((name) => GA_COOKIE_PATTERN.test(name));
  } catch {
    return;
  }
  const host = window.location.hostname;
  const domainSuffixes = [
    '',
    `; domain=${host}`,
    `; domain=.${host}`,
    ...GA_COOKIE_DOMAINS.map((domain) => `; domain=${domain}`),
  ];
  names.forEach((name) => {
    domainSuffixes.forEach((suffix) => {
      try {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; path=/${suffix}`;
      } catch {
        // Ignore
      }
    });
  });
};

// ---------------------------------------------------------------------------
// gtag loading
// ---------------------------------------------------------------------------

const loadGa4 = (): void => {
  const id = getGa4Id();
  if (!id || typeof document === 'undefined') {
    return;
  }

  // Re-enable in case the visitor declined earlier in this page session.
  (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = false;

  if (gtagLoaded) {
    window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
    return;
  }
  gtagLoaded = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js expects the arguments object itself, not an array copy.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };

  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('js', new Date());
  const fields = pageFields();
  setPageContext(fields);
  window.gtag('config', id, {
    // Page views are sent by trackPageView() with a scrubbed URL.
    send_page_view: false,
    page_location: fields.page_location,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  if (!document.getElementById(GTAG_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = GTAG_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
  }
};

const disableGa4 = (): void => {
  const id = getGa4Id();
  if (!id) {
    return;
  }
  (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = true;
  if (gtagLoaded) {
    window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
  }
  expireGaCookies();
};

/** Call once at startup: loads GA4 only if it is configured and already accepted. */
export const initAnalytics = (): void => {
  if (isAnalyticsEnabled()) {
    loadGa4();
  }
};

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/** Send a GA4 event. Returns false (and does nothing) when analytics is off. */
export const trackEvent = (name: string, params?: EventParams): boolean => {
  if (!isAnalyticsEnabled()) {
    return false;
  }
  loadGa4();
  const cleanParams: Record<string, string | number | boolean> = {};
  // Scrubbed URL on every event, so gtag never falls back to document.location.
  const { page_location, page_referrer } = pageFields();
  const source: EventParams = { page_location, page_referrer, ...params };
  Object.keys(source).forEach((key) => {
    let value = source[key];
    if ((key === 'page_location' || key === 'page_referrer') && typeof value === 'string') {
      value = stripUrl(value) || undefined;
    }
    if (key === 'page_path' && typeof value === 'string') {
      value = pathOnly(value);
    }
    if (value !== undefined) {
      cleanParams[key] = value;
    }
  });
  try {
    window.gtag?.('event', name, cleanParams);
    return true;
  } catch {
    return false;
  }
};

/**
 * Send a page view for the current route with origin + pathname only.
 * Called from <AnalyticsPageViews /> (App.tsx) on load, on route changes and
 * after Accept. No-op unless analytics is enabled.
 */
export const trackPageView = (pathname?: string): boolean => {
  if (!isAnalyticsEnabled()) {
    return false;
  }
  loadGa4();
  const fields = pageFields(pathname);
  setPageContext(fields);
  return trackEvent('page_view', {
    page_location: fields.page_location,
    page_path: fields.page_path,
    page_referrer: fields.page_referrer,
  });
};

export type CheckoutPlan = 'monthly' | 'annual';

const planForPriceId = (priceId: string): CheckoutPlan | undefined => {
  if (priceId && priceId === process.env.REACT_APP_STRIPE_PREMIUM_MONTHLY_PRICE_ID) return 'monthly';
  if (priceId && priceId === process.env.REACT_APP_STRIPE_PREMIUM_ANNUAL_PRICE_ID) return 'annual';
  return undefined;
};

export const trackMembershipView = (): void => {
  trackEvent('membership_view');
};

/**
 * Stripe checkout session was created and the browser is about to leave for
 * Stripe. Leaves a marker in sessionStorage (same tab survives the round
 * trip) so the success page knows a real checkout preceded it.
 */
export const trackBeginCheckout = (priceId: string): void => {
  const plan = planForPriceId(priceId);
  if (trackEvent('begin_checkout', { currency: 'USD', plan })) {
    writeStorage('session', CHECKOUT_MARKER_KEY, plan ?? 'unknown');
  }
};

/**
 * Checkout success return (/premium/success). Fires once per Stripe session
 * id when one is in the URL; otherwise only if this tab started a checkout,
 * so a direct visit to the page doesn't count as a purchase.
 */
export const trackPurchase = (sessionId: string | null): void => {
  if (!isAnalyticsEnabled()) {
    return;
  }
  const marker = readStorage('session', CHECKOUT_MARKER_KEY);

  if (sessionId) {
    if (readStorage('local', LAST_PURCHASE_KEY) === sessionId) {
      return;
    }
  } else if (!marker) {
    return;
  }

  const plan = marker === 'monthly' || marker === 'annual' ? marker : undefined;
  if (trackEvent('purchase', { transaction_id: sessionId ?? undefined, currency: 'USD', plan })) {
    writeStorage('session', CHECKOUT_MARKER_KEY, null);
    if (sessionId) {
      writeStorage('local', LAST_PURCHASE_KEY, sessionId);
    }
  }
};

/** Email sign-up: called once the account is created (before email confirmation). */
export const trackEmailSignUp = (): void => {
  trackEvent('sign_up', { method: 'email' });
};

/** The subset of a Supabase user that sign-up detection needs. */
export interface SignUpUserLike {
  id: string;
  created_at?: string | null;
  last_sign_in_at?: string | null;
  app_metadata?: { provider?: string } | null;
}

/**
 * True for an OAuth (Google / Apple) account whose first sign-in is happening
 * now. Email accounts are counted at sign-up time instead.
 */
export const isNewOAuthSignUp = (user: SignUpUserLike, now: number = Date.now()): boolean => {
  const provider = user.app_metadata?.provider;
  if (!provider || provider === 'email') {
    return false;
  }
  const created = user.created_at ? Date.parse(user.created_at) : NaN;
  const lastSignIn = user.last_sign_in_at ? Date.parse(user.last_sign_in_at) : NaN;
  if (Number.isNaN(created) || Number.isNaN(lastSignIn)) {
    return false;
  }
  return (
    Math.abs(lastSignIn - created) <= NEW_ACCOUNT_SIGN_IN_GAP_MS &&
    now - created <= NEW_ACCOUNT_MAX_AGE_MS
  );
};

/** On SIGNED_IN: send sign_up once for a brand-new OAuth account. */
export const trackOAuthSignUpIfNew = (user: SignUpUserLike | null | undefined): void => {
  if (!user || !isAnalyticsEnabled() || !isNewOAuthSignUp(user)) {
    return;
  }
  const key = `${SIGN_UP_SENT_PREFIX}${user.id}`;
  if (readStorage('local', key)) {
    return;
  }
  if (trackEvent('sign_up', { method: user.app_metadata?.provider })) {
    writeStorage('local', key, '1');
  }
};

/** Test-only: forget that gtag was loaded. */
export const __resetAnalyticsForTests = (): void => {
  gtagLoaded = false;
};
